import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { Avatar } from '@pickleballinc/ui/components/ui/avatar';
import clsx from 'clsx';

import { DEFAULT_PROFILE_IMAGE } from '@/modules/rankings/ppr/constants';
import {
  isPlayerInTeam,
  mapTournamentEventMatch,
  reorderTeamPlayers,
} from '@/modules/rankings/ppr/helpers';
import { PWRMobileScores } from '@/modules/rankings/ppr/ranking-history/PWRMobileScores';
import { PWRScores } from '@/modules/rankings/ppr/ranking-history/PWRScores';
import { TeamDisplay } from '@/modules/rankings/ppr/ranking-history/TeamDisplay';
import { TournamentEventMatch } from '@/modules/rankings/ppr/types';

interface TeamPlayer {
  firstName?: string | null;
  lastName?: string | null;
  imageUrl?: string | null;
}
const hasName = (player?: TeamPlayer | null) =>
  !!(player?.firstName?.trim() || player?.lastName?.trim());

const PlayerAvatar = ({
  player,
  isWinner,
  showInitials = true,
}: {
  player?: TeamPlayer | null;
  isWinner: boolean;
  showInitials?: boolean;
}) => (
  <Avatar
    firstName={showInitials ? player?.firstName : undefined}
    lastName={showInitials ? player?.lastName : undefined}
    src={player?.imageUrl ? getCDNURL(player.imageUrl, 80, 80, 0, true) : undefined}
    defaultSrc={getCDNURL(DEFAULT_PROFILE_IMAGE, 80, 80, 0, true)}
    className={clsx('bg-gray-300', isWinner && 'border-2 border-[#5925DC]')}
    textClassName='text-gray-700'
  />
);

interface PWRScoresDetailsProps {
  match: TournamentEventMatch;
  playerName?: string;
}

export const PWRScoresDetails = ({ match, playerName }: PWRScoresDetailsProps) => {
  const mappedMatch = mapTournamentEventMatch(match);

  const games = (
    [
      [match.teamOneGameOneScore, match.teamTwoGameOneScore],
      [match.teamOneGameTwoScore, match.teamTwoGameTwoScore],
      [match.teamOneGameThreeScore, match.teamTwoGameThreeScore],
      [match.teamOneGameFourScore, match.teamTwoGameFourScore],
      [match.teamOneGameFiveScore, match.teamTwoGameFiveScore],
    ] as [number, number][]
  ).filter(([a, b]) => !(a === 0 && b === 0));

  const teamOneGamesWon = games.filter(([a, b]) => a > b).length;
  const teamTwoGamesWon = games.filter(([a, b]) => b > a).length;
  const winner = teamOneGamesWon > teamTwoGamesWon ? 1 : teamTwoGamesWon > teamOneGamesWon ? 2 : 0;

  const input = (playerName || '').toLowerCase().trim();
  const isPlayerInTeamTwo =
    !!input &&
    !isPlayerInTeam(mappedMatch.teamOne, input) &&
    isPlayerInTeam(mappedMatch.teamTwo, input);

  const rawFirstTeam = isPlayerInTeamTwo ? mappedMatch.teamTwo : mappedMatch.teamOne;
  const firstTeam = reorderTeamPlayers(rawFirstTeam, playerName || '');
  const secondTeam = isPlayerInTeamTwo ? mappedMatch.teamOne : mappedMatch.teamTwo;
  const firstTeamWinner = isPlayerInTeamTwo ? winner === 2 : winner === 1;
  const secondTeamWinner = isPlayerInTeamTwo ? winner === 1 : winner === 2;

  return (
    <div className='w-full sm:border-l sm:border-gray-200'>
      <div className='flex items-center sm:h-full'>
        <div className='flex w-full flex-col'>
          <div className='flex border-b border-gray-200 pl-4 gap-x-2 items-center pb-3 pt-3'>
            <PlayerAvatar player={firstTeam?.playerOne} isWinner={firstTeamWinner} />
            {firstTeam?.playerTwo !== null && (
              <PlayerAvatar
                player={firstTeam?.playerTwo}
                isWinner={firstTeamWinner}
                showInitials={hasName(firstTeam?.playerOne)}
              />
            )}
            <div className='w-full text-sm sm:text-base text-gray-900'>
              <TeamDisplay team={firstTeam} playerName={playerName} separator=', ' />
            </div>
          </div>
          <PWRMobileScores match={match} swapped={isPlayerInTeamTwo} />
          <div className='flex pl-4 gap-x-2 items-center pb-3 pt-3 border-b border-gray-200 sm:border-b-0'>
            <PlayerAvatar player={secondTeam?.playerOne} isWinner={secondTeamWinner} />
            {secondTeam?.playerTwo !== null && (
              <PlayerAvatar
                player={secondTeam?.playerTwo}
                isWinner={secondTeamWinner}
                showInitials={hasName(secondTeam?.playerOne)}
              />
            )}
            <div className='w-full text-sm sm:text-base text-gray-900'>
              <TeamDisplay team={secondTeam} playerName={playerName} separator=', ' />
            </div>
          </div>
        </div>
        <PWRScores match={match} swapped={isPlayerInTeamTwo} />
        <div className='hidden w-[128px] border-b sm:block border-gray-200' />
      </div>
    </div>
  );
};
